import { useMutation } from '@tanstack/react-query'
import queryClient from '@/queryClient'
import { DocumentPage, updateDocumentPage } from '@/lib/api/client'
import QUERY_KEYS from './queryKeys'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'

// ---------------------------------------------------------------------------
// useMoveSubpage
// Reorders the template_values.subPages UUID array on the parent operation by
// swapping the target subpage with its neighbour in the given direction.
// Template attributes travel automatically — their subPageId UUID references
// are stable and do not need updating.
// ---------------------------------------------------------------------------

export const useMoveSubpage = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      parentPageId,
      subPageId,
      direction,
      parentPage,
    }: {
      parentPageId: string
      subPageId: string
      direction: 'up' | 'down'
      documentVersionId: string
      parentPage: DocumentPage
    }) => {
      const subPages: string[] =
        (parentPage.template_values?.subPages as string[] | undefined) ?? []
      const newSubPages = reorder(subPages, subPageId, direction)

      return updateDocumentPage(parentPageId, {
        name: parentPage.name,
        template_values: {
          ...parentPage.template_values,
          subPages: newSubPages,
        },
        parent_id: (parentPage.parent as string | null) ?? null,
      })
    },

    onMutate: async ({
      parentPageId,
      subPageId,
      direction,
      documentVersionId,
      parentPage,
    }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
      })

      const previousPages = queryClient.getQueryData<DocumentPage[]>([
        QUERY_KEYS.DOCUMENT_PAGES,
        { documentVersionId },
      ])

      const subPages: string[] =
        (parentPage.template_values?.subPages as string[] | undefined) ?? []
      const newSubPages = reorder(subPages, subPageId, direction)

      queryClient.setQueryData<DocumentPage[]>(
        [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        (pages) =>
          pages?.map((p) =>
            p.id === parentPageId
              ? {
                  ...p,
                  template_values: {
                    ...p.template_values,
                    subPages: newSubPages,
                  },
                }
              : p,
          ) as DocumentPage[],
      )

      return { previousPages }
    },

    onError: (error, { documentVersionId }, context) => {
      if (context?.previousPages) {
        queryClient.setQueryData(
          [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
          context.previousPages,
        )
      }
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },

    onSettled: (_, __, { documentVersionId }) => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
      })
    },
  })
}

function reorder(
  subPages: string[],
  subPageId: string,
  direction: 'up' | 'down',
): string[] {
  const idx = subPages.indexOf(subPageId)
  if (idx === -1) return subPages

  const targetIdx = direction === 'up' ? idx - 1 : idx + 1
  if (targetIdx < 0 || targetIdx >= subPages.length) return subPages

  const next = [...subPages]
  ;[next[idx], next[targetIdx]] = [next[targetIdx], next[idx]]
  return next
}
